// Formateadores de fecha/hora — locale fijo es-MX (dd/mm/yyyy).
// Uso OBLIGATORIO en todo el frontend para visualización de fechas.
// NO usar toLocaleDateString()/toLocaleString() sin locale en nuevo código.
// Ver /app/design_guidelines.md sección "Formato de fechas".

const LOCALE = "es-MX";

const _toDate = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const DATE_OPTS = { day: "2-digit", month: "2-digit", year: "numeric" };
const TIME_OPTS = { hour: "2-digit", minute: "2-digit", hour12: false };

// 08/05/2026
export const formatDate = (v, fallback = "-") => {
  const d = _toDate(v);
  return d ? d.toLocaleDateString(LOCALE, DATE_OPTS) : fallback;
};

// 08/05/2026 14:30
export const formatDateTime = (v, fallback = "-") => {
  const d = _toDate(v);
  if (!d) return fallback;
  return `${d.toLocaleDateString(LOCALE, DATE_OPTS)} ${d.toLocaleTimeString(LOCALE, TIME_OPTS)}`;
};

// 14:30
export const formatTime = (v, fallback = "-") => {
  const d = _toDate(v);
  return d ? d.toLocaleTimeString(LOCALE, TIME_OPTS) : fallback;
};

// 8 de mayo de 2026
export const formatDateLong = (v, fallback = "-") => {
  const d = _toDate(v);
  return d
    ? d.toLocaleDateString(LOCALE, { day: "numeric", month: "long", year: "numeric" })
    : fallback;
};

// YYYY-MM-DD para <input type="date"> o envío al backend. NO usar para display.
export const toISODate = (v) => {
  const d = _toDate(v);
  return d ? d.toISOString().slice(0, 10) : "";
};

// v4.59.5: Detección de "post-servicio" alineada con el backend.
// El backend (is_post_service) interpreta las fechas ISO sin zona como UTC.
// Aquí replicamos ese criterio: si la cadena no trae zona (Z/+hh:mm), la tratamos
// como UTC (append 'Z') para que el frontend y el backend coincidan exactamente.
// `ref` debe ser end_datetime (o pickup_datetime si no hay fin).
export const isPostServiceByDate = (ref) => {
  if (!ref) return false;
  let s = ref;
  if (typeof s === "string") {
    const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(s.trim());
    if (!hasTz) s = s.trim() + "Z";
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
};

// =============================================================================
// VEHICLE / UNIDAD label formatting — v4.55.0
// Mapeo vehicle_type técnico → etiqueta operativa legible.
// Cambios en este mapeo NO requieren tocar DB ni schema.
// =============================================================================
const VEHICLE_TYPE_LABELS = {
  sedan: "Sedán",
  suv: "Suburban", // operativamente el equipo identifica SUV como Suburban
  suburban: "Suburban",
  van: "Van",
  sprinter: "Sprinter",
  autobus: "Autobús",
  bus: "Autobús",
  camioneta: "Camioneta",
  minivan: "Minivan",
};

/**
 * Formatea la etiqueta visible de una unidad para dropdowns/selectores operativos.
 *
 * Prioridad:
 *   1. vehicle.name (ej. "Suburban 10" o "Suburban 2024")
 *   2. vehicle_type legible (ej. "Suburban" si vehicle_type="suv")
 *   3. "Unidad" como genérico
 *
 * Si existe vehicle.plate y showPlate=true → se agrega entre paréntesis.
 *
 * NO modifica vehicle ni envía nada al backend. Solo string de display.
 */
export const formatVehicleLabel = (vehicle, { showPlate = true } = {}) => {
  if (!vehicle) return "Unidad sin identificar";
  const name = (vehicle.name || "").trim();
  const typeRaw = String(vehicle.vehicle_type || "").trim().toLowerCase();
  const typeLabel = VEHICLE_TYPE_LABELS[typeRaw] || (vehicle.vehicle_type || "").trim();
  const plate = (vehicle.plate || "").trim();

  let primary;
  if (name) primary = name;
  else if (typeLabel) primary = typeLabel;
  else primary = "";

  if (!primary && !plate) return "Unidad sin identificar";
  if (!primary && plate) return `Unidad (${plate})`;
  if (showPlate && plate) return `${primary} (${plate})`;
  return primary;
};
